import { Event, Filter } from 'nostr-tools';
import {
  isReplaceableEvent,
  isEphemeralEvent,
  getReplaceableKey,
  shouldReplaceEvent,
  isValidEventStructure,
} from './utils/eventValidation';
import { eventMatchesFilter, extractTagKeys } from './utils/filterUtils';
import { EventCallback } from './types';

function isExpired(event: Event): boolean {
  const exp = event.tags.find((t) => t[0] === 'expiration')?.[1];
  if (!exp) return false;
  return Math.floor(Date.now() / 1000) > Number(exp);
}

/**
 * EventStore - Multi-indexed event storage with reactive subscriptions
 *
 * Stores all Nostr events in memory with fast O(1) lookups by:
 * - Event ID (primary index)
 * - Kind
 * - Author (pubkey)
 * - Tags (e:id, p:pubkey, etc.)
 *
 * Features:
 * - Automatic deduplication
 * - Replaceable event handling (keeps latest)
 * - Reactive subscriptions (callbacks on new matching events)
 * - Synchronous queries (no network, cache only)
 */
export class EventStore {
  // Primary storage
  private eventsById: Map<string, Event> = new Map();

  // Secondary indexes
  private eventsByKind: Map<number, Set<string>> = new Map();
  private eventsByAuthor: Map<string, Set<string>> = new Map();
  private eventsByTag: Map<string, Set<string>> = new Map();

  // Replaceable event tracking
  private replaceableKeys: Map<string, string> = new Map(); // key -> event ID

  // Deletion tracking (NIP-09)
  private deletedIds: Map<string, string> = new Map(); // target_event_id -> deleter_pubkey
  private processedDeletionEventIds: Set<string> = new Set();

  // Reactive subscriptions
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private listenerIdCounter = 0;

  /**
   * Add an event to the store
   * Returns true if event was added, false if rejected/duplicate
   */
  addEvent(event: Event): boolean {
    // Validate event structure
    if (!isValidEventStructure(event)) {
      console.warn('Invalid event structure:', event);
      return false;
    }

    // Handle deletion events (NIP-09) — process but don't store
    if (event.kind === 5) {
      if (this.processedDeletionEventIds.has(event.id)) return false;
      this.processedDeletionEventIds.add(event.id);
      for (const tag of event.tags) {
        if (tag[0] === 'e' && tag[1]) {
          this.deletedIds.set(tag[1], event.pubkey);
          const target = this.eventsById.get(tag[1]);
          if (target && target.pubkey === event.pubkey) {
            this.removeEvent(tag[1]);
          }
        }
      }
      this.notifyListeners(event);
      return true;
    }

    // Reject events that have already been deleted by their own author
    const deleterPubkey = this.deletedIds.get(event.id);
    if (deleterPubkey && deleterPubkey === event.pubkey) return false;

    // Don't store ephemeral events
    if (isEphemeralEvent(event.kind)) {
      // Still notify listeners even if not storing
      this.notifyListeners(event);
      return false;
    }

    // Handle replaceable events
    if (isReplaceableEvent(event.kind)) {
      const replaceableKey = getReplaceableKey(event);
      const existingEventId = this.replaceableKeys.get(replaceableKey);

      if (existingEventId) {
        const existingEvent = this.eventsById.get(existingEventId);
        if (existingEvent && !shouldReplaceEvent(event, existingEvent)) {
          // Existing event is newer, don't add
          return false;
        }

        // Remove old event
        if (existingEvent) {
          this.removeEvent(existingEventId);
        }
      }

      // Track new replaceable event
      this.replaceableKeys.set(replaceableKey, event.id);
    }

    // Check for exact duplicate
    if (this.eventsById.has(event.id)) {
      return false;
    }

    // Add to primary store
    this.eventsById.set(event.id, event);

    // Add to kind index
    if (!this.eventsByKind.has(event.kind)) {
      this.eventsByKind.set(event.kind, new Set());
    }
    this.eventsByKind.get(event.kind)!.add(event.id);

    // Add to author index
    if (!this.eventsByAuthor.has(event.pubkey)) {
      this.eventsByAuthor.set(event.pubkey, new Set());
    }
    this.eventsByAuthor.get(event.pubkey)!.add(event.id);

    // Add to tag indexes
    const tagKeys = extractTagKeys(event);
    for (const tagKey of tagKeys) {
      if (!this.eventsByTag.has(tagKey)) {
        this.eventsByTag.set(tagKey, new Set());
      }
      this.eventsByTag.get(tagKey)!.add(event.id);
    }

    // Notify reactive listeners
    this.notifyListeners(event);

    return true;
  }

  /**
   * Remove an event from the store
   */
  private removeEvent(eventId: string): void {
    const event = this.eventsById.get(eventId);
    if (!event) return;

    // Remove from primary store
    this.eventsById.delete(eventId);

    // Remove from kind index
    this.eventsByKind.get(event.kind)?.delete(eventId);

    // Remove from author index
    this.eventsByAuthor.get(event.pubkey)?.delete(eventId);

    // Remove from tag indexes
    const tagKeys = extractTagKeys(event);
    for (const tagKey of tagKeys) {
      this.eventsByTag.get(tagKey)?.delete(eventId);
    }
  }

  /**
   * Query events matching a filter (synchronous, cache only)
   * Returns events sorted by created_at (newest first)
   */
  query(filter: Filter): Event[] {
    let candidateIds: Set<string> | null = null;

    // Use indexes to narrow down candidates efficiently
    // Start with the most selective index

    // If IDs specified, that's the most selective
    if (filter.ids && filter.ids.length > 0) {
      candidateIds = new Set(filter.ids);
    }
    // If authors specified, use author index
    else if (filter.authors && filter.authors.length > 0) {
      const ids = new Set<string>();
      for (const author of filter.authors) {
        const authorEvents = this.eventsByAuthor.get(author);
        if (authorEvents) {
          authorEvents.forEach(id => ids.add(id));
        }
      }
      candidateIds = ids;
    }
    // If kinds specified, use kind index
    else if (filter.kinds && filter.kinds.length > 0) {
      const ids = new Set<string>();
      for (const kind of filter.kinds) {
        const kindEvents = this.eventsByKind.get(kind);
        if (kindEvents) {
          kindEvents.forEach(id => ids.add(id));
        }
      }
      candidateIds = ids;
    }
    // If tag filters, use tag index
    else {
      const tagFilters = Object.entries(filter).filter(([key]) => key.startsWith('#'));
      if (tagFilters.length > 0) {
        const [tagKey, tagValues] = tagFilters[0];
        const tagName = tagKey.slice(1);

        const ids = new Set<string>();
        for (const tagValue of tagValues as string[]) {
          const tagKey = `${tagName}:${tagValue}`;
          const tagEvents = this.eventsByTag.get(tagKey);
          if (tagEvents) {
            tagEvents.forEach(id => ids.add(id));
          }
        }
        candidateIds = ids;
      }
    }

    // If no index could be used, scan all events
    if (candidateIds === null) {
      candidateIds = new Set(this.eventsById.keys());
    }

    // Filter candidates and collect matching events
    const matchingEvents: Event[] = [];

    for (const eventId of Array.from(candidateIds)) {
      const event = this.eventsById.get(eventId);
      if (event && eventMatchesFilter(event, filter)) {
        const deleter = this.deletedIds.get(eventId);
        if (!deleter || deleter !== event.pubkey) {
          if (!isExpired(event)) matchingEvents.push(event);
        }
      }
    }

    // Sort by created_at (newest first)
    matchingEvents.sort((a, b) => b.created_at - a.created_at);

    // Apply limit if specified
    if (filter.limit && filter.limit > 0) {
      return matchingEvents.slice(0, filter.limit);
    }

    return matchingEvents;
  }

  /**
   * Get a single event by ID
   */
  getById(id: string): Event | undefined {
    const event = this.eventsById.get(id);
    if (!event) return undefined;
    const deleter = this.deletedIds.get(id);
    if (deleter && deleter === event.pubkey) return undefined;
    if (isExpired(event)) return undefined;
    return event;
  }

  isDeleted(id: string): boolean {
    return this.deletedIds.has(id);
  }

  /**
   * Subscribe to events matching a filter
   * Callback is invoked immediately for cached events, then for new matching events
   * Returns unsubscribe function
   */
  subscribe(filter: Filter, callback: EventCallback): () => void {
    // Generate unique listener ID
    const listenerId = `listener-${this.listenerIdCounter++}`;

    // Store listener with its filter
    this.listeners.set(listenerId, new Set([callback]));

    // Store filter with listener for matching
    (callback as any).__filter = filter;
    (callback as any).__listenerId = listenerId;

    // Immediately invoke callback with cached events
    const cachedEvents = this.query(filter);
    for (const event of cachedEvents) {
      callback(event);
    }

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listenerId);
    };
  }

  /**
   * Notify all listeners about a new event
   */
  private notifyListeners(event: Event): void {
    for (const [, callbacks] of Array.from(this.listeners.entries())) {
      for (const callback of Array.from(callbacks)) {
        const filter = (callback as any).__filter;
        if (filter && eventMatchesFilter(event, filter)) {
          callback(event);
        }
      }
    }
  }

  /**
   * Get statistics about the store
   */
  getStats() {
    const eventsByKind: Record<number, number> = {};
    for (const [kind, events] of Array.from(this.eventsByKind.entries())) {
      eventsByKind[kind] = events.size;
    }

    return {
      totalEvents: this.eventsById.size,
      eventsByKind,
      totalAuthors: this.eventsByAuthor.size,
      totalListeners: this.listeners.size,
    };
  }

  /**
   * Clear all events (useful for testing)
   */
  clear(): void {
    this.eventsById.clear();
    this.eventsByKind.clear();
    this.eventsByAuthor.clear();
    this.eventsByTag.clear();
    this.replaceableKeys.clear();
    this.deletedIds.clear();
    this.processedDeletionEventIds.clear();
  }

  /**
   * Get all events of a specific kind (for debugging)
   */
  getEventsByKind(kind: number): Event[] {
    const eventIds = this.eventsByKind.get(kind);
    if (!eventIds) return [];

    const events: Event[] = [];
    for (const id of Array.from(eventIds)) {
      const event = this.eventsById.get(id);
      if (event) events.push(event);
    }

    return events.sort((a, b) => b.created_at - a.created_at);
  }

  /**
   * Prune old events (except profiles and contact lists)
   * Remove events older than the specified age in days
   */
  pruneOldEvents(maxAgeDays: number = 7): number {
    const cutoffTime = Math.floor(Date.now() / 1000) - (maxAgeDays * 24 * 60 * 60);
    const eventsToRemove: string[] = [];

    for (const [eventId, event] of Array.from(this.eventsById.entries())) {
      // Don't prune profiles (kind 0) or contact lists (kind 3)
      if (event.kind === 0 || event.kind === 3) continue;

      // Remove if too old
      if (event.created_at < cutoffTime) {
        eventsToRemove.push(eventId);
      }
    }

    for (const eventId of eventsToRemove) {
      this.removeEvent(eventId);
    }

    return eventsToRemove.length;
  }
}
